import React, { useState } from "react";
import { toast } from "react-toastify";
import BranchSelect from "../../../components/ui/BranchSelect";
import Dialog from "../../../components/ui/Dialog";
import {
  useBondDefaults,
  useSetBondDefault,
} from "../../../api/queries/learningQueries";
import {
  useAllBranches,
  useCompanies,
} from "../../../api/queries/organizationQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../../utils/apiError";
import {
  PAYBACK_RULE,
  PAYBACK_RULE_HELP,
  paybackRuleLabel,
} from "../../../utils/trainingBond";

const EMPTY = [];
const RULES = Object.entries(PAYBACK_RULE);

/**
 * What a training with no payback rule of its own uses: its branch's (or the
 * nearest above), else its company's (or a parent's), else Reduces monthly.
 */
const BondDefaultsDialog = ({ onClose }) => {
  const user = useCurrentUser();
  const companies = useCompanies().data || EMPTY;
  const branches = useAllBranches().data || EMPTY;
  const [companyId, setCompanyId] = useState(user.companyId || "");
  const [branchId, setBranchId] = useState("");
  const [branchRule, setBranchRule] = useState("REDUCING");
  const query = useBondDefaults(companyId);
  const save = useSetBondDefault();
  const rows = Array.isArray(query.data) ? query.data : EMPTY;
  const companyRow = rows.find((r) => !r.branchId);
  const branchRows = rows.filter((r) => r.branchId);
  const branchName = (id) =>
    (Array.isArray(branches) ? branches : EMPTY).find((b) => b.id === id)
      ?.branchName || "Unknown branch";

  const set = (body, done) =>
    save.mutate(
      { companyId, ...body },
      {
        onSuccess: () => {
          toast.success("Default saved.");
          done?.();
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not save the default.")),
      },
    );

  return (
    <Dialog title="Payback defaults" onClose={onClose} busy={save.isPending}>
      <p className="fs-080 text-slate-600">
        What someone owes if they leave during a bond, for trainings with no
        rule of their own. A training uses its branch's rule or the nearest
        branch above, then its company's or a parent's, then Reduces monthly.{" "}
        {PAYBACK_RULE_HELP}
      </p>
      <p className="fs-080 fw-semibold">
        People who already accepted keep the rule they accepted.
      </p>

      <label htmlFor="bd-company" className="profile-label">
        Company
      </label>
      <select
        id="bd-company"
        className="form-select mb-3"
        value={companyId}
        onChange={(e) => {
          setCompanyId(e.target.value);
          setBranchId("");
        }}
      >
        <option value="">Select a company</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.companyName}
          </option>
        ))}
      </select>

      {query.isError && (
        <div className="alert alert-danger fs-080 py-2">
          {getApiErrorMessage(query.error, "Could not load the defaults.")}
        </div>
      )}

      {companyId && query.isSuccess && (
        <>
          <label htmlFor="bd-company-rule" className="profile-label">
            Company default
          </label>
          <select
            id="bd-company-rule"
            className="form-select mb-1"
            value={companyRow?.paybackRule || ""}
            disabled={save.isPending}
            onChange={(e) =>
              e.target.value && set({ paybackRule: e.target.value })
            }
          >
            {!companyRow && <option value="">Not set: Reduces monthly</option>}
            {RULES.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>

          <div className="profile-label mt-3">Branch defaults</div>
          {branchRows.length === 0 && (
            <div className="fs-080 text-slate-500 mb-2">None yet.</div>
          )}
          {branchRows.length > 0 && (
            <ul className="list-unstyled fs-085 mb-2">
              {branchRows.map((r) => (
                <li key={r.branchId}>
                  {branchName(r.branchId)}:{" "}
                  <strong>{paybackRuleLabel(r.paybackRule)}</strong>
                </li>
              ))}
            </ul>
          )}
          <div className="row g-2 align-items-end">
            <div className="col-sm-6">
              <label htmlFor="bd-branch" className="fs-075">
                Branch
              </label>
              <BranchSelect
                id="bd-branch"
                companyId={companyId}
                value={branchId}
                onChange={(v) => setBranchId(v || "")}
                emptyLabel="Choose a branch"
              />
            </div>
            <div className="col-sm-4">
              <label htmlFor="bd-branch-rule" className="fs-075">
                Rule
              </label>
              <select
                id="bd-branch-rule"
                className="form-select"
                value={branchRule}
                onChange={(e) => setBranchRule(e.target.value)}
              >
                {RULES.map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-2">
              <button
                type="button"
                className="btn btn-outline-primary w-100"
                disabled={!branchId || save.isPending}
                onClick={() =>
                  set({ branchId, paybackRule: branchRule }, () =>
                    setBranchId(""),
                  )
                }
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
};

export default BondDefaultsDialog;
